import { v4 as uuidv4 } from 'uuid'
import type { ClickEventPayload, ClickEventType, TrackEvent } from './types'

function getSessionId(): string {
  const key = '__tracker_sid__'
  let id = sessionStorage.getItem(key)
  if (!id) {
    id = uuidv4()
    sessionStorage.setItem(key, id)
  }
  return id
}

export class Tracker {
  private queue: TrackEvent[] = []
  private userId?: string
  private reporter?: (events: TrackEvent[]) => void

  // 点击事件独立处理：立即上报，不走批量队列
  private clickHandler?: (payload: ClickEventPayload) => void
  private clickQueue: ClickEventPayload[] = []

  setUser(userId: string) {
    this.userId = userId
  }

  setReporter(fn: (events: TrackEvent[]) => void) {
    this.reporter = fn
    // reporter 就绪后立即补发队列中积压的事件
    if (this.queue.length > 0) {
      this.flush()
    }
  }

  // 注入点击上报函数；ready 前积压的点击事件会在此时补发
  setClickHandler(fn: (payload: ClickEventPayload) => void) {
    this.clickHandler = fn
    if (this.clickQueue.length > 0) {
      const items = [...this.clickQueue]
      this.clickQueue = []
      for (const item of items) fn(item)
    }
  }

  track(type: TrackEvent['type'], extra?: Record<string, unknown>) {
    const event: TrackEvent = {
      type,
      path: location.pathname,
      title: document.title || undefined,
      userId: this.userId,
      sessionId: getSessionId(),
      timestamp: Date.now(),
      extra,
    }
    this.queue.push(event)
    // 不立即 flush，由 Reporter 定时批量上报
  }

  // 点击事件立即上报，不进批量队列
  // clickEventParentKey 默认取 clickEventKey 去掉最后一段，如 'menu.org.company.edit' → 'menu.org.company'
  trackClick(clickEventKey: string, clickEventType: ClickEventType, clickEventParentKey?: string) {
    const resolvedParentKey = clickEventParentKey ?? clickEventKey.split('.').slice(0, -1).join('.')
    const payload: ClickEventPayload = { clickEventKey, clickEventType, clickEventParentKey: resolvedParentKey }
    if (this.clickHandler) {
      this.clickHandler(payload)
    } else {
      // handler 未就绪时暂存，等 setClickHandler 时补发
      this.clickQueue.push(payload)
    }
  }

  flush(useBeacon = false) {
    if (this.queue.length === 0) return
    if (!this.reporter && !useBeacon) return // reporter 未就绪时暂存队列，等 setReporter 时补发
    const data = [...this.queue]
    this.queue = []
    if (useBeacon) {
      navigator.sendBeacon('/api/track', JSON.stringify(data))
    } else {
      this.reporter?.(data)
    }
  }
}
